import {
  alpha,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'

import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

type TProps = {
  open: boolean
  accentColor: string
  onConfirm: () => void
  onCancel: () => void
}

// Confirmation dialog for the "Start a new conversation" action (see the
// multi-tab persistence design) — sibling to EndChatDialog, but a distinct
// component: that dialog is specifically framed around LEAVING the chat
// (minimize/end/keep chatting), whereas this one is about starting fresh
// while staying open, triggered from its own header control rather than the
// close (X) button.
function NewConversationDialog({
  open,
  accentColor,
  onConfirm,
  onCancel,
}: TProps) {
  const theme = useTheme()
  const confirmTextColor = getWhiteOrBlackTextColorByBackground(
    accentColor,
    theme,
  )

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.mainFont, 0.35),
          },
        },
      }}
      PaperProps={{
        sx: {
          '&.MuiPaper-root': {
            borderRadius: rem(20),
          },
          padding: rem(8),
          margin: rem(20),
          marginTop: rem(24),
          width: '100%',
        },
      }}
    >
      <DialogContent sx={{ padding: 1.5 }}>
        <Stack gap={2}>
          <Stack gap={0.5}>
            <Typography
              sx={{
                fontSize: rem(20),
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.mainFont,
              }}
            >
              Start a new conversation?
            </Typography>
            <Typography
              sx={{
                fontSize: rem(15),
                color: theme.palette.mainFont,
              }}
            >
              This ends your current conversation and starts a fresh one — you
              won&apos;t be able to come back to this one.
            </Typography>
          </Stack>

          <Stack direction="row" gap={1.5}>
            <Button
              onClick={onCancel}
              sx={{
                flex: 1,
                borderRadius: rem(48),
                padding: `${rem(10)} ${rem(16)}`,
                textTransform: 'none',
                fontSize: rem(15),
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.mainFont,
                backgroundColor: theme.palette.common.white,
                border: `${rem(1)} solid ${theme.palette.tertiary5}`,
                '&:hover': {
                  backgroundColor: theme.palette.tertiary5,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              sx={{
                flex: 1,
                borderRadius: rem(48),
                padding: `${rem(12)} ${rem(16)}`,
                textTransform: 'none',
                fontSize: rem(15),
                fontWeight: theme.typography.fontWeightMedium,
                color: confirmTextColor,
                backgroundColor: accentColor,
                '&:hover': {
                  backgroundColor: accentColor,
                  opacity: 0.9,
                },
              }}
            >
              Start
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default NewConversationDialog
